import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoCloseSharp } from 'react-icons/io5';
import { getMenuSearchItems } from '../../utils/const';
import styles from './MenuSearch.module.css';

/* Поиск по пунктам меню личного кабинета (замена прежнего поиска по данным).
   В списке — только конечные пункты меню текущего пользователя:
   - соответствующие типу ЛК (постоянный / переменный состав);
   - для постоянного состава — доступные по правам;
   - без группирующих разделов.
   Клик по пункту — тот же переход, что из основной навигации. */

const MenuSearch = ({ onClose }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isTeacher = useSelector(state => state.auth.isTeacher);
  const accessModes = useSelector(state => state.auth.access_modes);

  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const items = useMemo(
    () => getMenuSearchItems(isTeacher, accessModes),
    [isTeacher, accessModes]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = items.map(item => ({ ...item, label: t(item.name) }));
    if (!q) return list;
    return list.filter(item => item.label.toLowerCase().includes(q));
  }, [items, query, t]);

  // Esc закрывает, страница под окном не прокручивается, компенсируем скроллбар
  useEffect(() => {
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    if (inputRef.current) inputRef.current.focus();

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const go = (to) => {
    history.push(to);
    onClose();
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.panel} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <span className={styles.title}>Поиск</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
            <IoCloseSharp size={20} />
          </button>
        </div>

        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Введите название раздела"
        />

        <div className={styles.list}>
          {filtered.length ? (
            filtered.map(item => (
              <button
                key={item.to}
                type="button"
                className={styles.item}
                onClick={() => go(item.to)}
              >
                {item.label}
              </button>
            ))
          ) : (
            <div className={styles.empty}>Ничего не найдено</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MenuSearch;
